import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RangeShootingReasonClient, RangeShootingReasonDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getApiClient = () => new RangeShootingReasonClient(API_BASE_URL, axiosInstance);

export const useRangeShootingReasons = () =>
  useQuery({
    queryKey: ['rangeShootingReasons'],
    queryFn: async () => {
      const response = await getApiClient().searchRangeShootingReasons({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

export const useRangeShootingReasonSearch = () => {
  const search = async (params: any) => {
    const response = await getApiClient().searchRangeShootingReasons(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

export const useRangeShootingReasonById = (id: string | undefined) =>
  useQuery({
    queryKey: ['rangeShootingReason', id],
    queryFn: async () => {
      const response = await getApiClient().getRangeShootingReasonById(id!);
      return response?.data || null;
    },
    enabled: !!id,
  });

export const useCreateRangeShootingReason = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RangeShootingReasonDTO) => getApiClient().createRangeShootingReason(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rangeShootingReasons'] }),
  });
};

export const useUpdateRangeShootingReason = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RangeShootingReasonDTO }) =>
      getApiClient().updateRangeShootingReason(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rangeShootingReasons'] }),
  });
};

export const useDeleteRangeShootingReason = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getApiClient().deleteRangeShootingReason(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rangeShootingReasons'] }),
  });
};